/**
 * Settings slice — Redux Toolkit state + thunk for the public storefront
 * settings endpoint(s).
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   shipping: ShippingSettings | null,
 *   isLoadingShipping: boolean,
 *   shippingError: string | null,
 *   shippingFetchedAt: number | null,
 * }
 * ```
 *
 * Invariants
 * ----------
 * - The slice is shared across `/cart` and `/checkout` so the threshold
 *   only needs to be fetched once per session. The {@link fetchShippingSettings}
 *   thunk carries a `condition` guard that resolves to a no-op when a
 *   prior fetch has already populated `shipping` OR when an in-flight
 *   request is already pending.
 * - Failures degrade silently: the slice keeps `shipping = null`, sets
 *   `shippingError` for diagnostics, and the consuming UI (the
 *   FreeShippingIndicator container) renders nothing. The thunk does NOT
 *   throw to its dispatcher — it `rejectWithValue`s the message and lets
 *   the reducer keep the slice valid.
 *
 * Per `.claude/rules/frontend-data-flow.md`:
 *  - Capsules MUST NOT import this slice. Containers do.
 *  - Errors caught from the api-client are `ApiError` instances; we
 *    project the `.message` onto `shippingError` so containers don't have
 *    to care about the envelope details for this read-only surface.
 *
 * SF-SHIP-002.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import * as settingsApi from '@dfq/shared/api/settings';
import { ApiError } from '@dfq/shared/api/_envelope';
import type { HomepageSettings, NavigationSettings, ShippingSettings } from '@dfq/shared/types/settings';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export interface SettingsState {
  /** Cached shipping settings; `null` until first fetch resolves. */
  shipping: ShippingSettings | null;
  /** True while a shipping settings request is in flight. */
  isLoadingShipping: boolean;
  /**
   * Last shipping settings error (best-effort string). Containers may log
   * this but the FreeShippingIndicator silently treats any error as "no
   * threshold" and renders nothing.
   */
  shippingError: string | null;
  /**
   * Wall-clock timestamp (ms) of the last successful shipping fetch.
   * Currently unused for cache invalidation — admin changes propagate on
   * next page load — but exposed for future TTL logic.
   */
  shippingFetchedAt: number | null;
  /**
   * True after the first fetch attempt (success or failure). The condition
   * guard checks this to avoid a retry storm when the endpoint returns 404
   * — without it, a failed fetch leaves `shipping = null`, the guard
   * allows re-dispatch, and the container's isLoading toggle re-triggers
   * the effect on every render cycle.
   */
  shippingFetchAttempted: boolean;

  /** Cached homepage chooser settings; `null` until first fetch resolves. */
  homepage: HomepageSettings | null;
  /** True while a homepage settings request is in flight. */
  isLoadingHomepage: boolean;
  /** Last homepage settings error (best-effort string). */
  homepageError: string | null;
  /** True after the first fetch attempt (success or failure) — see shipping's guard. */
  homepageFetchAttempted: boolean;

  /** Cached navigation (header/footer) settings; `null` until first fetch resolves. */
  navigation: NavigationSettings | null;
  /** True while a navigation settings request is in flight. */
  isLoadingNavigation: boolean;
  /** Last navigation settings error (best-effort string). */
  navigationError: string | null;
  /** True after the first fetch attempt (success or failure) — see shipping's guard. */
  navigationFetchAttempted: boolean;
}

const initialState: SettingsState = {
  shipping: null,
  isLoadingShipping: false,
  shippingError: null,
  shippingFetchedAt: null,
  shippingFetchAttempted: false,

  homepage: null,
  isLoadingHomepage: false,
  homepageError: null,
  homepageFetchAttempted: false,

  navigation: null,
  isLoadingNavigation: false,
  navigationError: null,
  navigationFetchAttempted: false,
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

interface SettingsRootStateSlice {
  settings: SettingsState;
}

function rejectionMessageFromUnknown(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown settings error';
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/settings/shipping` — fetch the admin-configured shipping
 * settings (free-shipping threshold, rush surcharge, carrier toggles).
 *
 * Carries a `condition` guard so multiple containers (Cart sidebar +
 * Checkout summary) dispatching on mount don't duplicate the request:
 *  - skip when a fetch is already in flight (`isLoadingShipping`);
 *  - skip when the slice already has cached `shipping` (idempotent within
 *    a single session — admin changes propagate on next reload).
 */
export const fetchShippingSettings = createAsyncThunk<
  ShippingSettings,
  void,
  { state: SettingsRootStateSlice; rejectValue: string }
>('settings/fetchShipping', async (_, { rejectWithValue }) => {
  try {
    return await settingsApi.fetchShippingSettings();
  } catch (err) {
    return rejectWithValue(rejectionMessageFromUnknown(err));
  }
}, {
  condition: (_arg, { getState }) => {
    const slice = getState().settings;
    if (slice.isLoadingShipping) return false;
    if (slice.shipping !== null) return false;
    // Skip when a prior attempt already resolved or failed — prevents a
    // retry storm when the endpoint returns 404 (shipping stays null but
    // the fetch is done for this session).
    if (slice.shippingFetchAttempted) return false;
    return true;
  },
});

/**
 * `GET /api/v1/settings/homepage` — fetch the "Start your project" chooser
 * copy (hero, value strip, section headings, numbered steps).
 *
 * Carries the same `condition` guard shape as {@link fetchShippingSettings}:
 * skip when a fetch is in flight or already resolved/failed this session.
 */
export const fetchHomepageSettings = createAsyncThunk<
  HomepageSettings,
  void,
  { state: SettingsRootStateSlice; rejectValue: string }
>('settings/fetchHomepage', async (_, { rejectWithValue }) => {
  try {
    return await settingsApi.fetchHomepageSettings();
  } catch (err) {
    return rejectWithValue(rejectionMessageFromUnknown(err));
  }
}, {
  condition: (_arg, { getState }) => {
    const slice = getState().settings;
    if (slice.isLoadingHomepage) return false;
    if (slice.homepage !== null) return false;
    if (slice.homepageFetchAttempted) return false;
    return true;
  },
});

/**
 * `GET /api/v1/settings/navigation` — fetch the admin-configured header
 * links + footer columns/copy (SF-NAV-101).
 *
 * Carries the same `condition` guard shape as {@link fetchShippingSettings}:
 * skip when a fetch is in flight or already resolved/failed this session.
 */
export const fetchNavigationSettings = createAsyncThunk<
  NavigationSettings,
  void,
  { state: SettingsRootStateSlice; rejectValue: string }
>('settings/fetchNavigation', async (_, { rejectWithValue }) => {
  try {
    return await settingsApi.fetchNavigationSettings();
  } catch (err) {
    return rejectWithValue(rejectionMessageFromUnknown(err));
  }
}, {
  condition: (_arg, { getState }) => {
    const slice = getState().settings;
    if (slice.isLoadingNavigation) return false;
    if (slice.navigation !== null) return false;
    if (slice.navigationFetchAttempted) return false;
    return true;
  },
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    /** Reset the slice — used by logout flows / tests. */
    resetSettings() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchShippingSettings.pending, (state) => {
        state.isLoadingShipping = true;
        state.shippingError = null;
      })
      .addCase(fetchShippingSettings.fulfilled, (state, action) => {
        state.isLoadingShipping = false;
        state.shipping = action.payload;
        state.shippingError = null;
        state.shippingFetchedAt = Date.now();
        state.shippingFetchAttempted = true;
      })
      .addCase(fetchShippingSettings.rejected, (state, action) => {
        state.isLoadingShipping = false;
        state.shipping = null;
        state.shippingError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch shipping settings';
        state.shippingFetchAttempted = true;
      })
      .addCase(fetchHomepageSettings.pending, (state) => {
        state.isLoadingHomepage = true;
        state.homepageError = null;
      })
      .addCase(fetchHomepageSettings.fulfilled, (state, action) => {
        state.isLoadingHomepage = false;
        state.homepage = action.payload;
        state.homepageError = null;
        state.homepageFetchAttempted = true;
      })
      .addCase(fetchHomepageSettings.rejected, (state, action) => {
        state.isLoadingHomepage = false;
        state.homepage = null;
        state.homepageError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch homepage settings';
        state.homepageFetchAttempted = true;
      })
      .addCase(fetchNavigationSettings.pending, (state) => {
        state.isLoadingNavigation = true;
        state.navigationError = null;
      })
      .addCase(fetchNavigationSettings.fulfilled, (state, action) => {
        state.isLoadingNavigation = false;
        state.navigation = action.payload;
        state.navigationError = null;
        state.navigationFetchAttempted = true;
      })
      .addCase(fetchNavigationSettings.rejected, (state, action) => {
        state.isLoadingNavigation = false;
        state.navigation = null;
        state.navigationError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch navigation settings';
        state.navigationFetchAttempted = true;
      });
  },
});

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

const selectSettingsState = (state: SettingsRootStateSlice): SettingsState =>
  state.settings;

/** Cached shipping settings, or `null` when not yet fetched / failed. */
export const selectShippingSettings = createSelector(
  [selectSettingsState],
  (s): ShippingSettings | null => s.shipping,
);

/**
 * Convenience selector — returns the free-shipping threshold (or `null`
 * when the admin has not configured one, the API failed, or the fetch
 * has not yet resolved). The FreeShippingIndicator container reads this
 * directly.
 */
export const selectFreeShippingThreshold = createSelector(
  [selectSettingsState],
  (s): number | null => s.shipping?.freeShippingThreshold ?? null,
);

/**
 * Whether delivery is billed as its own line.
 *
 * Defaults to `false` until settings load: the safe default is the one that
 * never invents a charge the customer was not quoted.
 */
export const selectChargeShipping = createSelector(
  [selectSettingsState],
  (s): boolean => s.shipping?.chargeShipping ?? false,
);

/**
 * Standard turnaround, business days, or `null` when not configured
 * (SF-STICKER-105). The product-page turnaround/shipping strip reads this.
 */
export const selectDefaultLeadTimeDays = createSelector(
  [selectSettingsState],
  (s): number | null => s.shipping?.defaultLeadTimeDays ?? null,
);

/** Rush turnaround, business days, or `null` when not configured. */
export const selectRushLeadTimeDays = createSelector(
  [selectSettingsState],
  (s): number | null => s.shipping?.rushLeadTimeDays ?? null,
);

export const selectIsLoadingShippingSettings = createSelector(
  [selectSettingsState],
  (s): boolean => s.isLoadingShipping,
);

export const selectShippingSettingsError = createSelector(
  [selectSettingsState],
  (s): string | null => s.shippingError,
);

export const selectShippingFetchAttempted = createSelector(
  [selectSettingsState],
  (s): boolean => s.shippingFetchAttempted,
);

/** Cached homepage chooser settings, or `null` when not yet fetched / failed. */
export const selectHomepageSettings = createSelector(
  [selectSettingsState],
  (s): HomepageSettings | null => s.homepage,
);

export const selectIsLoadingHomepageSettings = createSelector(
  [selectSettingsState],
  (s): boolean => s.isLoadingHomepage,
);

export const selectHomepageSettingsError = createSelector(
  [selectSettingsState],
  (s): string | null => s.homepageError,
);

/** Cached navigation (header/footer) settings, or `null` when not yet fetched / failed. */
export const selectNavigationSettings = createSelector(
  [selectSettingsState],
  (s): NavigationSettings | null => s.navigation,
);

export const selectIsLoadingNavigationSettings = createSelector(
  [selectSettingsState],
  (s): boolean => s.isLoadingNavigation,
);

export const selectNavigationSettingsError = createSelector(
  [selectSettingsState],
  (s): string | null => s.navigationError,
);

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export const { resetSettings } = settingsSlice.actions;
export { settingsSlice };
export const settingsReducer = settingsSlice.reducer;
export default settingsReducer;
